import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Checkbox, CircularProgress } from '@mui/material';
import { toast } from 'react-toastify';
import { clinicalAnalyticsActions } from 'store/clinical';
import { DateTypeSelector, NotError } from 'components';
import { Images } from 'utils';
import { timeRangePresets, displayModeEnums, goalStatusList, goalStatusColors } from './constants';
import { ChartInfoPopover } from './components/chartInfoPopover';
import { GoalProgressChart } from './components/goalProgressChart';
import { GoalDrilldown } from './goalDrilldown';
import { GET_GOALS_PROGRESS } from 'store/clinical/clinicalAnalytics/clinicalAnalytics.types';
import moment from 'moment';

const buildPresetDateRange = (months, baseDate = moment()) => {
    const date = baseDate.clone();

    return {
        start: date.clone().subtract(months, 'months').format('YYYY-MM-DD'),
        end: date.format('YYYY-MM-DD'),
    };
};

const clampRangeToToday = (startDate, endDate) => {
    const today = moment().endOf('day');
    const safeStart = startDate ? moment(startDate) : null;
    const safeEnd = endDate ? moment(endDate) : null;

    if (!safeStart?.isValid() || !safeEnd?.isValid()) {
        return null;
    }

    return {
        start: moment.min(safeStart, today).format('YYYY-MM-DD'),
        end: moment.min(safeEnd, today).format('YYYY-MM-DD'),
    };
};

const triggerDownload = (url, fileName) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

const canOpenGoalDrilldown = (goal) => Array.isArray(goal?.cumulativeProgress?.data) && goal.cumulativeProgress.data.length > 0;

const getGoalStatusClassName = (status) => (status ? `status-${String(status).toLowerCase()}` : 'status-default');

const hasGoalProgressData = (goal) =>
    Array.isArray(goal?.cumulativeProgress?.data) &&
    goal.cumulativeProgress.data.some((point) => (point?.cumulativeCount || 0) > 0 || (point?.cumulative || 0) > 0);

export const DetailedInsightsTab = ({ clientId, clientName }) => {
    const dispatch = useDispatch();
    const goalsProgress = useSelector((state) => state.clinicalAnalytics.goalsProgress);
    const domainList = useSelector((state) => state.domains.domainList);
    const isLoading = useSelector((state) => state.httpOnLoad?.some((item) => item?.loadingType === GET_GOALS_PROGRESS));

    const [timeRange, setTimeRange] = useState(6);
    const [displayMode, setDisplayMode] = useState(displayModeEnums.COUNT);
    const [statusFilter, setStatusFilter] = useState('');
    const [domainFilter, setDomainFilter] = useState('');
    const [dateRange, setDateRange] = useState(() => buildPresetDateRange(6));
    const [selectedGoalId, setSelectedGoalId] = useState(null);
    const goalChartRefs = useRef({});
    const [includeTargets, setIncludeTargets] = useState(true);
    const [exportProgress, setExportProgress] = useState(null);
    const exportCancelledRef = useRef(false);
    // Closing the drawer (unmount) mid-export cancels it instead of downloading a PDF later.
    useEffect(
        () => () => {
            exportCancelledRef.current = true;
        },
        []
    );
    const sortedGoalsProgress = [...(goalsProgress || [])].sort((left, right) => {
        const leftHasData = hasGoalProgressData(left);
        const rightHasData = hasGoalProgressData(right);

        if (leftHasData === rightHasData) {
            return (left?.goalName || '').localeCompare(right?.goalName || '');
        }

        return leftHasData ? -1 : 1;
    });

    const fetchGoalsProgress = useCallback(() => {
        if (!clientId) return;

        const params = {
            startDate: dateRange.start,
            endDate: dateRange.end,
        };

        if (statusFilter) params.status = statusFilter;
        if (domainFilter) params.domain = domainFilter;

        dispatch(clinicalAnalyticsActions.getGoalsProgress(clientId, params));
    }, [clientId, dateRange.end, dateRange.start, dispatch, domainFilter, statusFilter]);

    useEffect(() => {
        fetchGoalsProgress();
    }, [fetchGoalsProgress]);

    const handleTimeRangeChange = (months) => {
        setTimeRange(months);
        setDateRange(buildPresetDateRange(months));
    };

    const handleDateRangeChange = (params) => {
        if (!params?.startDate || !params?.endDate) return;

        const nextRange = clampRangeToToday(params.startDate, params.endDate);
        if (!nextRange) return;

        setDateRange(nextRange);
        setTimeRange(null);
    };

    const handleDownloadGoalChart = (event, goalId, goalName) => {
        event.stopPropagation();

        const chartInstance = goalChartRefs.current[goalId]?.getEchartsInstance?.();
        if (!chartInstance) return;

        const imageUrl = chartInstance.getDataURL({
            type: 'png',
            pixelRatio: 2,
            backgroundColor: '#FFFFFF',
            excludeComponents: ['toolbox'],
        });

        triggerDownload(imageUrl, `${goalName || 'goal-progress'}-${goalId}-${moment().format('YYYYMMDD-HHmmss')}.png`);
    };

    const handleDownloadPdf = async () => {
        if (exportProgress || !sortedGoalsProgress.length) return;
        exportCancelledRef.current = false;
        setExportProgress({ phase: 'render', current: 0, total: sortedGoalsProgress.length });

        let exportModule = null;
        try {
            // Loaded on click: keeps jspdf (~100 KB gzip) out of the main bundle.
            exportModule = await import('./insightsPdfExport');
            const { captureChartRef, exportClinicalInsightsPdf } = exportModule;
            const isCancelled = () => exportCancelledRef.current;

            // Waits for each chart to be drawn: one that just remounted (filter change) or is
            // still animating (#/% switch) would otherwise be captured blank or half-drawn.
            const goalImages = {};
            await Promise.all(
                sortedGoalsProgress.map(async (goal) => {
                    goalImages[goal.goalId] = await captureChartRef(() => goalChartRefs.current[goal.goalId], isCancelled);
                })
            );

            await exportClinicalInsightsPdf({
                clientId,
                clientName,
                goals: sortedGoalsProgress,
                goalImages,
                includeTargets,
                filters: {
                    dateRange,
                    statusLabel: goalStatusList.find((status) => status.value === statusFilter)?.label || 'All',
                    domainLabel: domainList?.find((domain) => domain.id === domainFilter)?.name || 'All Domains',
                    displayModeLabel: displayMode === displayModeEnums.PERCENT ? '% (cumulative)' : '# (count)',
                },
                onProgress: setExportProgress,
                isCancelled,
            });
        } catch (error) {
            if (!(exportModule && error instanceof exportModule.InsightsExportCancelled)) {
                toast(<NotError type={'error'} errMessage={error?.message || 'Failed to create the PDF'} />);
            }
        } finally {
            setExportProgress(null);
        }
    };

    const exportProgressLabel = !exportProgress
        ? ''
        : exportProgress.phase === 'targets'
        ? `Loading targets ${exportProgress.current}/${exportProgress.total}…`
        : `Building PDF ${exportProgress.current}/${exportProgress.total}…`;

    const handleOpenGoalDrilldown = (goal) => {
        if (!canOpenGoalDrilldown(goal)) return;
        dispatch(clinicalAnalyticsActions.resetGoalDrilldown());
        setSelectedGoalId(goal.goalId);
    };

    const handleBackToGoals = () => {
        dispatch(clinicalAnalyticsActions.resetGoalDrilldown());
        setSelectedGoalId(null);
    };

    if (selectedGoalId) {
        return (
            <GoalDrilldown
                clientId={clientId}
                goalId={selectedGoalId}
                dateRange={dateRange}
                displayMode={displayMode}
                onBack={handleBackToGoals}
            />
        );
    }

    return (
        <div>
            <div className="insights-filter-bar">
                <span className="insights-filter-heading">
                    {displayMode === displayModeEnums.PERCENT ? 'Cumulative ' : ''}Progress Per Goal
                </span>

                <div className="insights-time-range-group">
                    {timeRangePresets.map((preset) => (
                        <button
                            key={preset.value}
                            className={`insights-time-range-btn ${timeRange === preset.value ? 'active' : ''}`}
                            onClick={() => handleTimeRangeChange(preset.value)}
                        >
                            {preset.label}
                        </button>
                    ))}
                </div>

                <div className="insights-display-toggle">
                    <button
                        className={`insights-display-btn ${displayMode === displayModeEnums.COUNT ? 'active' : ''}`}
                        onClick={() => setDisplayMode(displayModeEnums.COUNT)}
                    >
                        #
                    </button>
                    <button
                        className={`insights-display-btn ${displayMode === displayModeEnums.PERCENT ? 'active' : ''}`}
                        onClick={() => setDisplayMode(displayModeEnums.PERCENT)}
                    >
                        %
                    </button>
                </div>

                <DateTypeSelector
                    startName="startDate"
                    endName="endDate"
                    filters={{ startDate: dateRange.start, endDate: dateRange.end }}
                    handleGetDates={handleDateRangeChange}
                />
            </div>

            <div className="insights-pdf-export-bar">
                <label className="insights-pdf-export-option">
                    <Checkbox
                        size="small"
                        checked={includeTargets}
                        disabled={!!exportProgress}
                        onChange={(event) => setIncludeTargets(event.target.checked)}
                        style={{ padding: 0 }}
                    />
                    <span>Include target graphs</span>
                </label>
                {exportProgress ? (
                    <>
                        <span className="insights-pdf-export-progress" role="status">
                            <CircularProgress size={14} />
                            {exportProgressLabel}
                        </span>
                        <button type="button" className="insights-pdf-export-btn" onClick={() => (exportCancelledRef.current = true)}>
                            Cancel
                        </button>
                    </>
                ) : (
                    <button
                        type="button"
                        className="insights-pdf-export-btn"
                        disabled={isLoading || !sortedGoalsProgress.length}
                        onClick={handleDownloadPdf}
                        title="Download every graph shown below, with the current filters, as one PDF"
                    >
                        <img src={Images.downloadCloudBlue} alt="" className="insights-export-icon" />
                        Download PDF
                    </button>
                )}
            </div>

            <div className="insights-status-pills">
                {goalStatusList.map((status) => (
                    <button
                        key={status.value}
                        className={`insights-status-pill ${statusFilter === status.value ? 'active' : ''}`}
                        onClick={() => setStatusFilter(status.value)}
                    >
                        {status.value && (
                            <span className={`status-badge-dot ${getGoalStatusClassName(status.value)} insights-inline-status-dot`} />
                        )}
                        {status.label}
                    </button>
                ))}

                <select className="insights-domain-select" value={domainFilter} onChange={(event) => setDomainFilter(event.target.value)}>
                    <option value="">All Domains</option>
                    {domainList?.map((domain) => (
                        <option key={domain.id} value={domain.id}>
                            {domain.name}
                        </option>
                    ))}
                </select>
            </div>

            {!goalsProgress?.length && isLoading ? (
                <div className="insights-loading">
                    <CircularProgress size={32} />
                </div>
            ) : goalsProgress.length === 0 ? (
                <div className="insights-empty">
                    <p>No goals match the current filters</p>
                </div>
            ) : (
                <div className="insights-chart-card-with-overlay">
                    {sortedGoalsProgress.map((goal) => (
                        <div
                            key={goal.goalId}
                            className={`goal-progress-card ${canOpenGoalDrilldown(goal) ? '' : 'goal-progress-card-disabled'}`}
                            onClick={() => handleOpenGoalDrilldown(goal)}
                        >
                            <div className="goal-progress-card-header">
                                <div className="goal-progress-card-left">
                                    <div className="insights-chart-title-row">
                                        <p className="goal-progress-card-name">{goal.goalName}</p>
                                        <ChartInfoPopover
                                            compact={true}
                                            title="Goal Chart Guide"
                                            items={[
                                                {
                                                    label: 'Mastery',
                                                    text: 'Shows cumulative mastered target progress for this goal.',
                                                    style: { background: '#0C66E4' },
                                                },
                                                {
                                                    label: 'Trend',
                                                    text: 'Shows the overall direction of progress when enough data exists.',
                                                    style: { background: '#FF6B6B' },
                                                },
                                                {
                                                    label: 'Mastery',
                                                    text: 'Dashed threshold line showing the mastery benchmark.',
                                                    type: 'dash',
                                                    style: { borderTopColor: '#00C48C' },
                                                },
                                                {
                                                    label: 'Client Events',
                                                    text: 'Vertical markers for client-level events within the selected range.',
                                                    type: 'vertical',
                                                    style: { borderColor: '#0C66E4' },
                                                },
                                                {
                                                    label: 'Goal Events',
                                                    text: 'Vertical markers for goal-level events within the selected range.',
                                                    type: 'vertical',
                                                    style: { borderColor: '#2A8E6D' },
                                                },
                                            ]}
                                        />
                                    </div>
                                    <p className="goal-progress-card-domain">{goal.domain}</p>
                                </div>
                                <div className="goal-progress-card-right">
                                    <span className="goal-progress-card-mastered">
                                        <strong>{goal.masteredCount}</strong> mastered
                                    </span>
                                    <span className={`status-badge ${getGoalStatusClassName(goal.status)}`}>
                                        <span className={`status-badge-dot ${getGoalStatusClassName(goal.status)}`} />
                                        {goal.status}
                                    </span>
                                    <button
                                        className="insights-export-btn insights-export-btn-inline"
                                        onClick={(event) => handleDownloadGoalChart(event, goal.goalId, goal.goalName)}
                                    >
                                        <img src={Images.downloadCloudBlue} alt="download" className="insights-export-icon" />
                                    </button>
                                </div>
                            </div>

                            <GoalProgressChart
                                chartRef={(node) => {
                                    goalChartRefs.current[goal.goalId] = node;
                                }}
                                data={goal.cumulativeProgress?.data || []}
                                events={goal.cumulativeProgress?.events || []}
                                displayMode={displayMode}
                                goalMastery={goal.goalMastery}
                                totalCount={goal.totalTargets}
                            />
                        </div>
                    ))}
                    {isLoading && (
                        <div className="insights-chart-loading-overlay">
                            <CircularProgress size={28} />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
